import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { MousePointer2 } from 'lucide-react';

const CardShuffler = () => {
    const [cards, setCards] = useState([
        { id: 1, title: 'Wycena bazowa', val: 'Zakończona' },
        { id: 2, title: 'Koszty ukryte', val: '0 PLN' },
        { id: 3, title: 'Cena na umowie', val: 'Zablokowana' }
    ]);
    const containerRef = useRef(null);

    useEffect(() => {
        const interval = setInterval(() => {
            setCards(prev => {
                const newArr = [...prev];
                const last = newArr.pop();
                newArr.unshift(last);
                return newArr;
            });
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div ref={containerRef} className="relative w-full h-48 flex items-center justify-center mb-6 perspective-1000">
            {cards.map((c, i) => {
                const isTop = i === 0;
                return (
                    <div
                        key={c.id}
                        className="absolute w-full p-4 rounded-xl border border-dark/10 shadow-sm transition-all duration-700 flex justify-between items-center"
                        style={{
                            backgroundColor: isTop ? '#F5F3EE' : i === 1 ? '#E8E4DD' : '#D1CEc7',
                            color: isTop ? '#111' : '#555',
                            transform: `translateY(${i * 12}px) scale(${1 - i * 0.05}) translateZ(${-i * 20}px)`,
                            zIndex: 10 - i,
                            opacity: 1 - i * 0.2,
                            // The exact physics-based spring curve specified in memory
                            transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
                        }}
                    >
                        <span className="font-mono text-sm uppercase">{c.title}</span>
                        <span className={`font-mono font-bold text-sm ${isTop && c.id === 2 ? 'text-accent' : ''}`}>{c.val}</span>
                    </div>
                );
            })}
        </div>
    );
};

const Typewriter = () => {
    const words = "Zero kompromisów. Pełna    izolacja.             Certyfikowane materiały. Pełna precyzja.";
    const [text, setText] = useState("");
    const [cursorObj, setCursor] = useState(true);

    useEffect(() => {
        let i = 0;
        let timer;
        const type = () => {
            if (i < words.length) {
                // Simulate typing pauses
                const char = words.charAt(i);
                const delay = char === '.' ? 400 : char === ' ' ? 20 : Math.random() * 50 + 20;

                setText(prev => prev + char);
                i++;
                timer = setTimeout(type, delay);
            } else {
                timer = setTimeout(() => {
                    setText("");
                    i = 0;
                    type();
                }, 4000);
            }
        };
        timer = setTimeout(type, 1000);

        const cursorTimer = setInterval(() => setCursor(c => !c), 500);

        return () => {
            clearTimeout(timer);
            clearInterval(cursorTimer);
        };
    }, []);

    return (
        <div className="w-full h-48 bg-dark rounded-[2rem] p-6 flex flex-col mb-6 relative overflow-hidden shadow-inner border border-dark/20">
            <div className="flex items-center space-x-2 mb-4">
                <div className="w-2.5 h-2.5 rounded-full bg-accent animate-pulse shadow-[0_0_8px_rgba(230,59,46,0.6)]"></div>
                <span className="text-primary/70 font-mono text-[10px] uppercase tracking-widest">Live Telemetry</span>
            </div>
            <p className="font-mono text-primary/90 text-sm leading-relaxed whitespace-pre-wrap">
                <span className="text-accent opacity-80 mr-2">$&gt;</span>
                {text}
                <span className={`inline-block w-2 bg-accent ml-1 transition-opacity duration-100 ${cursorObj ? 'opacity-100' : 'opacity-0'}`}>&nbsp;</span>
            </p>
        </div>
    );
};

const Scheduler = () => {
    const cursorRef = useRef(null);
    const containerRef = useRef(null);
    const [activeDay, setActiveDay] = useState(-1);
    const [saved, setSaved] = useState(false);
    const days = ['P', 'W', 'Ś', 'C', 'P', 'S'];

    useEffect(() => {
        let ctx = gsap.context(() => {
            const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.5 });

            tl.set(cursorRef.current, { x: 20, y: 120, opacity: 0, scale: 1 })
                .call(() => { setActiveDay(-1); setSaved(false); })
                // Enter
                .to(cursorRef.current, { opacity: 1, duration: 0.4, ease: "power2.out" })
                // Move to Wednesday (index 2)
                .to(cursorRef.current, { x: 100, y: 40, duration: 1.2, ease: "power3.inOut" })
                // Click (scale down)
                .to(cursorRef.current, { scale: 0.8, duration: 0.1, ease: "power1.in" })
                // Activate day
                .call(() => setActiveDay(2))
                // Release click (scale up)
                .to(cursorRef.current, { scale: 1, duration: 0.15, ease: "power1.out", delay: 0.05 })
                // Move to Save button
                .to(cursorRef.current, { x: 220, y: 130, duration: 1, ease: "power3.inOut", delay: 0.4 })
                // Click Save
                .to(cursorRef.current, { scale: 0.8, duration: 0.1, ease: "power1.in" })
                // Highlight Save
                .call(() => setSaved(true))
                // Release click
                .to(cursorRef.current, { scale: 1, duration: 0.15, ease: "power1.out", delay: 0.05 })
                // Exit
                .to(cursorRef.current, { opacity: 0, duration: 0.4, ease: "power2.in", delay: 0.5 })
                .call(() => { setActiveDay(-1); setSaved(false); });

        }, containerRef);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={containerRef} className="w-full h-48 border border-dark/10 rounded-[2rem] p-6 mb-6 flex flex-col relative bg-primary/20 overflow-hidden">
            <span className="font-mono text-[10px] uppercase tracking-widest mb-4 text-dark/50">Protokół Rezerwacji</span>

            <div className="flex justify-between w-full mt-2 relative">
                {days.map((d, i) => (
                    <div
                        key={i}
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-sm transition-all duration-300 ${activeDay === i ? 'bg-accent text-background shadow-[0_4px_12px_rgba(230,59,46,0.3)] scale-110' : 'bg-transparent text-dark/60'}`}
                    >
                        {d}
                    </div>
                ))}
            </div>

            <div className={`absolute bottom-5 right-5 px-5 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-widest transition-all duration-300 ${saved ? 'bg-accent text-background shadow-[0_4px_12px_rgba(230,59,46,0.3)]' : 'bg-dark text-primary'}`}>
                Zatwierdź
            </div>

            <div ref={cursorRef} className="absolute z-10 origin-top-left" style={{ filter: 'drop-shadow(0px 8px 12px rgba(0,0,0,0.3))' }}>
                <MousePointer2 size={28} fill="#111111" stroke="#ffffff" strokeWidth={1.5} />
            </div>
        </div>
    );
};

export default function Features() {
    const sectionRef = useRef(null);

    useEffect(() => {
        let ctx = gsap.context(() => {
            gsap.from(".feature-card", {
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 75%",
                },
                y: 60,
                opacity: 0,
                duration: 1,
                stagger: 0.15,
                ease: "power3.out"
            });
        }, sectionRef);
        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} id="oferta" className="py-24 px-6 md:px-16 bg-background text-dark relative">
            <div className="max-w-7xl mx-auto">
                <div className="mb-16">
                    <h2 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tight mb-4 text-dark">
                        Dlaczego my?
                    </h2>
                    <p className="font-body text-lg text-dark/70 max-w-2xl">
                        Odpowiadamy na największe lęki inwestorów. Zero prowizorek, pełna przejrzystość finansowa.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">

                    {/* Card 1 */}
                    <div className="feature-card bg-primary p-8 rounded-[3rem] border border-dark/5 flex flex-col">
                        <CardShuffler />
                        <h3 className="text-2xl font-heading font-medium tracking-tight mb-2">Koniec z dopłacaniem "po drodze".</h3>
                        <p className="font-body text-dark/70 text-sm xl:text-base">
                            Działamy na podstawie szczegółowego pomiaru i rzetelnego kosztorysu. Cena robocizny zapisana na umowie jest ostateczna. Płacisz dokładnie tyle, na ile się umawialiśmy, bez niespodzianek przy rozliczeniu.
                        </p>
                    </div>

                    {/* Card 2 */}
                    <div className="feature-card bg-primary p-8 rounded-[3rem] border border-dark/5 flex flex-col">
                        <Typewriter />
                        <h3 className="text-2xl font-heading font-medium tracking-tight mb-2">Własna technologia i automatyzacja.</h3>
                        <p className="font-body text-dark/70 text-sm xl:text-base">
                            Nasza ekipa nie używa tanich zamienników. Posiadamy własną technologię i profesjonalne automaty zgrzewające, zachowując najwyższe standardy krycia papą. Otrzymujesz na to pisemną gwarancję na lata: bezwzględna szczelność i maksymalna izolacja.
                        </p>
                    </div>

                    {/* Card 3 */}
                    <div className="feature-card bg-primary p-8 rounded-[3rem] border border-dark/5 flex flex-col">
                        <Scheduler />
                        <h3 className="text-2xl font-heading font-medium tracking-tight mb-2">Brak przestojów pogodowych.</h3>
                        <p className="font-body text-dark/70 text-sm xl:text-base">
                            Mamy wypracowane rabaty w hurtowniach i w pełni odpowiadamy za logistykę materiałów. Wchodzimy na obiekt, sprawnie realizujemy zlecenie bez zbędnych przestojów, a po sobie zostawiamy idealny porządek.
                        </p>
                    </div>

                </div>
            </div>
        </section>
    );
}
